class AnnouncementBarCarousel extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.slides = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.sliderWrapper = this.querySelector('.announcement-bar-slider');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || this.slides.length < 2) return;

    this.currentIndex = 0;
    this.autoplayEnabled = this.slider.dataset.autoplay === 'true';
    this.autoplaySpeed = (parseInt(this.slider.dataset.speed, 10) || 5) * 1000;
    this.animationDelay = 250;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.autoplayTimer = null;
    this.userPaused = false;
    this.isTransitioning = false;

    this.prevButton?.addEventListener('click', () => this.navigate('previous'));
    this.nextButton?.addEventListener('click', () => this.navigate('next'));

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.resumeAutoplay());
    this.addEventListener('focusin', (event) => {
      if (this.contains(event.target)) this.pauseAutoplay();
    });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.resumeAutoplay();
    });

    this.boundOnResize = this.onResize.bind(this);

    this.setStableHeight();
    this.heightObserver = new ResizeObserver(() => this.setStableHeight());
    this.heightObserver.observe(this.slider);

    window.addEventListener('resize', this.boundOnResize);

    this.reducedMotion.addEventListener('change', () => {
      if (this.reducedMotion.matches) {
        this.pauseAutoplay();
      } else {
        this.resumeAutoplay();
      }
    });

    this.setSlideAccessibility(0);
    this.startAutoplayIfEnabled();
  }

  disconnectedCallback() {
    this.pauseAutoplay();
    this.heightObserver?.disconnect();
    window.removeEventListener('resize', this.boundOnResize);
  }

  onResize() {
    window.requestAnimationFrame(() => this.setStableHeight());
  }

  setStableHeight() {
    let maxHeight = 0;

    this.slides.forEach((slide) => {
      slide.classList.add('announcement-bar__slide--measuring');
      maxHeight = Math.max(maxHeight, slide.offsetHeight);
      slide.classList.remove('announcement-bar__slide--measuring');
    });

    if (maxHeight > 0) {
      this.slider.style.minHeight = `${maxHeight}px`;
    }
  }

  setSlideAccessibility(activeIndex) {
    this.slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      slide.querySelectorAll('a').forEach((link) => {
        link.setAttribute('tabindex', isActive ? '0' : '-1');
      });
    });
  }

  navigate(direction) {
    if (this.isTransitioning) return;

    this.userPaused = true;
    this.pauseAutoplay();

    const delta = direction === 'next' ? 1 : -1;
    const nextIndex =
      (this.currentIndex + delta + this.slides.length) % this.slides.length;

    this.goToSlide(nextIndex, direction);
  }

  goToSlide(nextIndex, direction = 'next', animate = true) {
    if (nextIndex === this.currentIndex || this.isTransitioning) return;

    const currentSlide = this.slides[this.currentIndex];
    const nextSlide = this.slides[nextIndex];
    const shouldAnimate =
      animate && !this.reducedMotion.matches && currentSlide !== nextSlide;

    if (shouldAnimate) {
      this.isTransitioning = true;
      this.applyFadeAnimation(currentSlide, nextSlide, direction);

      setTimeout(() => {
        this.currentIndex = nextIndex;
        this.setSlideAccessibility(nextIndex);
        this.isTransitioning = false;
      }, this.animationDelay * 2);
    } else {
      this.currentIndex = nextIndex;
      this.setSlideAccessibility(nextIndex);
    }
  }

  applyFadeAnimation(currentSlide, nextSlide, direction) {
    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    nextSlide.setAttribute('aria-hidden', 'false');
    currentSlide.classList.add('announcement-bar__slide--animating');
    nextSlide.classList.add('announcement-bar__slide--animating');

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(
        `${animationClassOut}-${direction}`,
        'announcement-bar__slide--animating'
      );
      nextSlide.classList.remove(
        `${animationClassIn}-${direction}`,
        'announcement-bar__slide--animating'
      );
    }, this.animationDelay * 2);
  }

  autoRotate() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex, 'next');
  }

  startAutoplayIfEnabled() {
    if (
      this.autoplayEnabled &&
      !this.userPaused &&
      !this.reducedMotion.matches
    ) {
      this.playAutoplay();
    }
  }

  playAutoplay() {
    this.pauseAutoplay();
    this.slider.setAttribute('aria-live', 'off');
    this.autoplayTimer = setInterval(
      () => this.autoRotate(),
      this.autoplaySpeed
    );
  }

  pauseAutoplay() {
    clearInterval(this.autoplayTimer);
    this.autoplayTimer = null;
    this.slider.setAttribute('aria-live', 'polite');
  }

  resumeAutoplay() {
    if (
      !this.userPaused &&
      this.autoplayEnabled &&
      !this.reducedMotion.matches
    ) {
      this.playAutoplay();
    }
  }
}

customElements.define('announcement-bar-carousel', AnnouncementBarCarousel);
